/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps,
    IContentBlockViewProps,
    ILinksData
} from '@msdyn365-commerce-modules/content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IImageSettings } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';

interface IExtendedContentBlockConfig {
    ctaLinks?: ILinksData[];
    imageGradient?: boolean;
    imageAspectRatio?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    msdyn365__moduleLayout?: string;
}

// Image configurations for different aspect ratios
// Only dimensions are hardcoded - mode, lazyload, and quality will be taken from Site Builder.
const imageConfigurations: Record<string, IImageSettings> = {
    ratio3to1: {
        viewports: {
            xs: { w: 575, h: 192 },
            sm: { w: 768, h: 256 },
            md: { w: 992, h: 331 },
            lg: { w: 1440, h: 480 },
            xl: { w: 1920, h: 640 }
        },
        cropFocalRegion: true,
        mode: 5
    },
    ratio6to1: {
        viewports: {
            xs: { w: 575, h: 96 },
            sm: { w: 768, h: 128 },
            md: { w: 992, h: 166 },
            lg: { w: 1440, h: 240 },
            xl: { w: 1920, h: 320 }
        },
        cropFocalRegion: true,
        mode: 5
    },
    ratio12to1: {
        viewports: {
            xs: { w: 575, h: 48 },
            sm: { w: 768, h: 64 },
            md: { w: 992, h: 83 },
            lg: { w: 1440, h: 120 },
            xl: { w: 1920, h: 160 }
        },
        cropFocalRegion: true,
        mode: 5
    },
    ratio3to2: {
        viewports: {
            xs: { w: 531, h: 354 },
            sm: { w: 708, h: 472 },
            md: { w: 932, h: 622 },
            lg: { w: 1380, h: 920 },
            xl: { w: 1698, h: 1132 }
        },
        cropFocalRegion: true,
        mode: 5
    },
    ratio1to1: {
        viewports: {
            xs: { w: 575, h: 575 },
            sm: { w: 708, h: 708 },
            md: { w: 932, h: 932 },
            lg: { w: 1380, h: 1380 },
            xl: { w: 1698, h: 1698 }
        },
        cropFocalRegion: true,
        mode: 5
    }
};

/**
 * Merges hardcoded dimensions with Site Builder configuration.
 * @param baseSettings - Base image settings with hardcoded dimensions.
 * @param siteBuilderSettings - Settings from Site Builder (quality, lazyload).
 * @returns Merged image settings.
 */
const mergeImageSettings = (baseSettings: IImageSettings, siteBuilderSettings: IImageSettings): IImageSettings => {
    const merged: IImageSettings = { ...baseSettings };

    // Only override if Site Builder has defined values
    if (siteBuilderSettings.lazyload !== undefined) {
        merged.lazyload = siteBuilderSettings.lazyload;
    }
    if (siteBuilderSettings.mode !== undefined) {
        merged.mode = siteBuilderSettings.mode;
    }
    if (siteBuilderSettings.quality !== undefined) {
        merged.quality = siteBuilderSettings.quality;
    }

    // Build dynamic query strings for each viewport with quality and mode
    const quality = merged.quality ?? 80;
    const mode = merged.mode ?? 5;

    if (merged.viewports) {
        Object.keys(merged.viewports).forEach(viewport => {
            const vp = merged.viewports[viewport as keyof typeof merged.viewports];
            if (vp && vp.w && vp.h) {
                vp.q = `w=${vp.w}&h=${vp.h}&q=${quality}&m=${mode}&f=webp&cropfocalregion=true`;
            }
        });
    }

    return merged;
};
/**
 * Get image settings based on the selected aspect ratio.
 * @param aspectRatio - The selected aspect ratio name.
 * @param siteBuilderSettings - Settings from Site Builder image configuration.
 * @returns The corresponding image settings.
 */
const getImageSettingsForAspectRatio = (aspectRatio?: string, siteBuilderSettings?: IImageSettings): IImageSettings | undefined => {
    if (!aspectRatio || !siteBuilderSettings) {
        return undefined;
    }
    const baseSettings = imageConfigurations[aspectRatio];
    return baseSettings ? mergeImageSettings(baseSettings, siteBuilderSettings) : undefined;
};

interface IExtendedContentBlockViewProps extends IContentBlockViewProps {
    config: IContentBlockViewProps['config'] & IExtendedContentBlockConfig;
}

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Render CTA Links.
 * @param ctaLinks - Array of CTA links.
 * @returns JSX Element.
 */
const renderCtaLinks = (ctaLinks: ILinksData[]) => {
    // Filter out any links that don't have a valid destinationUrl to avoid SSR errors
    const safeLinks = (ctaLinks || []).filter(link => !!link && !!link.linkUrl && !!link.linkUrl.destinationUrl);

    if (safeLinks.length === 0) {
        return null;
    }

    return (
        <div className='ms-content-block__cta-links'>
            {safeLinks.map((link, index) => (
                <a
                    key={index}
                    className='ms-content-block__cta-link-item'
                    href={link.linkUrl?.destinationUrl}
                    target={link.openInNewTab ? '_blank' : undefined}
                    rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                >
                    {link.linkText}
                </a>
            ))}
        </div>
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
const contentBlockView: React.FC<IExtendedContentBlockViewProps> = props => {
    // Get the selected image aspect ratio from module configuration
    const extendedConfig = props.config as typeof props.config & IExtendedContentBlockConfig;
    const selectedImageRatio = extendedConfig.imageAspectRatio;

    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent,
        config
    } = props;

    // Get Site Builder image settings from the image field and merge with hardcoded dimensions
    // @ts-expect-error - Accessing props to get imageSettings
    const siteBuilderSettings = image?.props?.imageSettings;
    const imageSettings = getImageSettingsForAspectRatio(selectedImageRatio, siteBuilderSettings);

    const { ctaLinks, imageGradient } = config;

    // Apply image settings if available
    const imageElement = imageSettings ? React.cloneElement(image as React.ReactElement, { imageSettings }) : image;

    // @ts-expect-error HTML element need to be clear on run time.
    const hasImage: boolean = image.props.src;

    if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{imageElement}</Node>
                </Node>
                <Node {...detailsContainer}>
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            {hasImage && (
                <Node
                    {...imageContainer}
                    className={classnames(imageContainer.className, imageGradient && 'ms-content-block__image-gradient')}
                >
                    {imageElement}
                </Node>
            )}
            <Node className={detailsContainer.className}>
                {title}
                {text}
                {links}
                {additionalContent && renderAdditionalContent(additionalContent)}
                {ctaLinks && renderCtaLinks(ctaLinks)}
            </Node>
        </Module>
    );
};

export default contentBlockView;
